import { useAuth } from "../../context/AuthContext";
import {
  BarChart3,
  Clock3,
  FileText,
  Phone,
  X,
} from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { CopyButton } from "../../components/ui/copy-button";
import { formatDuration, formatTimestamp } from "../../lib/time";
import { CORE } from "../../lib/api";
import { cn } from "../../lib/utils";
import { SENTIMENT_META, STATUS_META, type CallRecord } from "./types";
import { TranscriptConversation } from "./transcript-conversation";
import { useRecordingUrl } from "./use-recording-url";

function DetailRow({ label, value, copy }: { label: string; value: string; copy?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-4 border-t border-line px-4 py-3 first:border-t-0">
      <span className="text-xs text-ink-3">{label}</span>
      <span className="inline-flex min-w-0 items-center gap-1 text-right font-mono text-xs font-medium text-ink">
        <span className="max-w-[360px] truncate">{value || "Not available"}</span>
        {copy && value && <CopyButton value={value} />}
      </span>
    </div>
  );
}

export function CallDetailsPanel({ call, onClose }: { call: CallRecord; onClose: () => void }) {
  const { user } = useAuth() as any;
  const status = STATUS_META[call.status];
  const sentiment = SENTIMENT_META[call.sentiment];
  
  const signedUrl = useRecordingUrl(call.recordingUrl, call.callId);

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/20" onClick={onClose} />
      <aside className="fixed bottom-0 right-0 top-0 z-50 flex w-full max-w-[640px] flex-col border-l border-line bg-canvas shadow-2xl">
        <header className="flex items-start justify-between border-b border-line bg-elevated p-6">
          <div className="min-w-0 pr-8">
            <p className="text-xs font-semibold uppercase text-ink-3">Call details</p>
            <h2 className="mt-1 truncate text-xl font-semibold text-ink">
              {call.campaign || call.contactId}
            </h2>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Badge tone={status.tone}>{status.label}</Badge>
              <Badge tone={sentiment.tone}>{sentiment.label}</Badge>
              <span className="text-sm text-ink-3">{formatTimestamp(call.createdAt, user?.timezone)}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close call details"
            className="rounded-lg p-2 text-ink-3 hover:bg-soft"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="custom-scrollbar flex-1 space-y-7 overflow-y-auto p-6">
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-lg border border-line bg-elevated p-4">
              <Clock3 className="h-4 w-4 text-ink-3" />
              <p className="mt-2 text-xs text-ink-3">Duration</p>
              <p className="mt-1 font-mono text-lg font-semibold text-ink">
                {formatDuration(call.durationSec)}
              </p>
            </div>
            <div className="rounded-lg border border-line bg-elevated p-4">
              <Phone className="h-4 w-4 text-ink-3" />
              <p className="mt-2 text-xs text-ink-3">Hung by</p>
              <p className="mt-1 text-lg font-semibold capitalize text-ink">{call.hungBy}</p>
            </div>
            <div className="rounded-lg border border-line bg-elevated p-4">
              <BarChart3 className="h-4 w-4 text-ink-3" />
              <p className="mt-2 text-xs text-ink-3">Outcomes</p>
              <p className="mt-1 text-lg font-semibold text-ink">{call.outcomes.length}</p>
            </div>
          </div>

          {signedUrl && (
            <div className="mb-6">
              <h3 className="mb-3 text-xs font-semibold uppercase text-ink-3">Recording</h3>
              <audio controls preload="metadata" src={signedUrl} className="h-10 w-full" />
            </div>
          )}

          <div>
            <h3 className="mb-3 text-xs font-semibold uppercase text-ink-3">Call information</h3>
            <div className="overflow-hidden rounded-lg border border-line bg-elevated">
              <DetailRow label="Direction" value={call.direction === "inbound" ? "Inbound" : "Outbound"} />
              {call.destinationDid && <DetailRow label="Destination DID" value={call.destinationDid} copy />}
              <DetailRow label="Contact ID" value={call.contactId} copy />
              <DetailRow label="Phone" value={call.phone} copy />
              <DetailRow label="Execution ID" value={call.executionId} copy />
              <DetailRow label="Schedule ID" value={call.scheduleId} copy />
              <DetailRow label="Agent" value={call.agent} />
              <DetailRow label="Version" value={call.version} />
              <DetailRow label="Voice" value={call.voice} />
            </div>
          </div>

          <div>
            <h3 className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase text-ink-3">
              <BarChart3 className="h-4 w-4" /> Extracted outcomes
            </h3>
            {call.outcomes.length > 0 ? (
              <div className="grid grid-cols-2 gap-3">
                {call.outcomes.map((outcome) => (
                  <div key={outcome.field} className="rounded-lg border border-line bg-elevated p-3">
                    <p className="truncate text-[10px] font-semibold uppercase text-ink-3">
                      {outcome.field}
                    </p>
                    <p className="mt-1 break-words text-sm font-semibold text-ink">{outcome.value}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="rounded-lg border border-dashed border-line bg-elevated p-5 text-sm text-ink-3">
                No outcomes were extracted for this call.
              </p>
            )}
          </div>

          <div>
            <h3 className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase text-ink-3">
              <FileText className="h-4 w-4" /> AI summary
            </h3>
            <div className="rounded-lg border border-line bg-elevated p-4">
              {call.aiSummary.length > 0 ? (
                <ul className="space-y-2">
                  {call.aiSummary.map((line, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-ink-2">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-orange-500" />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-ink-3">No AI summary is available.</p>
              )}
            </div>
          </div>

          <div>
            <h3 className="mb-3 text-xs font-semibold uppercase text-ink-3">Transcript</h3>
            <TranscriptConversation call={call} />
          </div>
        </div>
      </aside>
    </>
  );
}
